import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";
import { formatApiError } from "@/lib/api-error";
import type { UserWithProfile, UserProfile } from "@/types/user-profile";

interface Agencia {
  id: string;
  nome: string;
}

interface AssignProfileModalProps {
  user: UserWithProfile | null;
  profiles: UserProfile[];
  onClose: () => void;
  onSuccess: () => void;
}

export function AssignProfileModal({
  user,
  profiles,
  onClose,
  onSuccess,
}: AssignProfileModalProps) {
  const [selectedProfileId, setSelectedProfileId] = useState<string>("none");
  const [agenciaId, setAgenciaId] = useState<string>("none");
  const [agencias, setAgencias] = useState<Agencia[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Verifica se o perfil selecionado é de agência
  const selectedProfile = profiles.find((p) => p.id.toString() === selectedProfileId);
  const isAgencyProfile = selectedProfile?.slug?.includes("agencia") ?? false;

  useEffect(() => {
    setSelectedProfileId(user?.access_profile_id?.toString() || "none");
    setAgenciaId(user?.agencia_id || "none");
  }, [user]);

  // Buscar agências quando necessário
  useEffect(() => {
    if (isAgencyProfile && agencias.length === 0) {
      supabase
        .from("agencias")
        .select("id, nome")
        .order("nome")
        .then(({ data }) => {
          if (data) setAgencias(data);
        });
    }
  }, [isAgencyProfile, agencias.length]);

  // Reset agencia quando muda perfil para não-agência
  useEffect(() => {
    if (!isAgencyProfile) {
      setAgenciaId("none");
    }
  }, [isAgencyProfile]);

  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isAgencyProfile && agenciaId === "none") {
      toast({
        title: "Erro",
        description: "Selecione uma agência para perfis de agência.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Writes nas tabelas de identidade vão pela API (a migration 133 trancou
      // user_profile_assignments e profiles de terceiros no /rest/v1).
      const profileId =
        selectedProfileId && selectedProfileId !== "none"
          ? parseInt(selectedProfileId)
          : null;
      const newAgenciaId =
        isAgencyProfile && agenciaId !== "none" ? agenciaId : null;

      await apiFetch(`${SUPABASE_URL}admin/usuarios/${user.id}/perfil`, {
        method: "PUT",
        body: JSON.stringify({ profile_id: profileId, agencia_id: newAgenciaId }),
      });

      toast({
        title: "Sucesso",
        description: "Perfil atualizado com sucesso!",
      });

      onSuccess();
      onClose();
    } catch (error: unknown) {
      console.error("Erro ao atualizar perfil:", error);
      const { detail } = formatApiError(error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o perfil do usuário. " + detail,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeProfiles = profiles.filter((p) => p.is_active);

  return (
    <Dialog open={!!user} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Alterar Perfil do Usuário</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Usuário</Label>
            <div className="text-sm font-medium">
              {user.full_name || user.username || "Sem nome"}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile">Perfil</Label>
            <Select
              value={selectedProfileId}
              onValueChange={setSelectedProfileId}
            >
              <SelectTrigger id="profile">
                <SelectValue placeholder="Selecione um perfil" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem perfil</SelectItem>
                {activeProfiles.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id.toString()}>
                    {profile.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isAgencyProfile && (
            <div className="space-y-2">
              <Label htmlFor="agencia">Agência *</Label>
              <Select value={agenciaId} onValueChange={setAgenciaId}>
                <SelectTrigger id="agencia">
                  <SelectValue placeholder="Selecione uma agência" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selecione...</SelectItem>
                  {agencias.map((agencia) => (
                    <SelectItem key={agencia.id} value={agencia.id}>
                      {agencia.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
